// src/eden/core/edenClient.js
// Eden API client (v1.1)
// - Sends { message, context, meta, intent } to backend
// - Bearer = the Eden session's live token (edenSession.js); never a token
//   carried in page context, and nothing is sent without a confirmed account
// - CRA + Vite env support
// - Returns normalized Eden response contract always

import { normalizeEdenResponse } from "./responseContract";
import {
  edenSessionBearer,
  getEdenSessionAccount,
  getEdenSessionGeneration,
  isCurrentEdenSession,
} from "./edenSession";
import { evaluateEdenConfidentialityRisk } from "./confidentialityGuardrails";

const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ??
  process.env.REACT_APP_API_BASE_URL ??
  "";

let edenConversationState = {
  lastConfirmedChannel: null,
};

export const EDEN_SESSION_UNCONFIRMED_REPLY =
  "I can't confirm which account is signed in on this page, so nothing was sent. Sign in again or reload the page, then ask again.";

/** Forget carried conversation state: it belongs to the Eden session that ended. */
export function resetEdenConversationState() {
  edenConversationState = { lastConfirmedChannel: null };
}

/** Normalize base URL (no trailing slash) */
function normalizeBaseUrl(url) {
  if (!url) return "";
  return String(url).replace(/\/+$/, "");
}

/** Basic safe fetch wrapper with timeout */
async function fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(id);
  }
}

/**
 * Eden request
 * @param {Object} params
 * @param {string} params.message - user message
 * @param {Object} params.context - Eden context payload
 * @param {Object} [params.meta] - optional metadata (ui state, selection ids, etc.)
 * @param {string} [params.intent] - optional explicit intent hint
 * @param {number} [params.timeoutMs] - request timeout
 */
export async function edenAsk({
  message,
  context,
  meta = {},
  intent = null,
  timeoutMs = 30000,
} = {}) {
  const base = normalizeBaseUrl(API_BASE_URL);
  if (!base) {
    return {
      ok: false,
      error: "Missing backend URL (REACT_APP_BACKEND_URL or REACT_APP_API_BASE_URL).",
      status: 0,
      data: normalizeEdenResponse(null),
    };
  }

  if (!message || typeof message !== "string") {
    return {
      ok: false,
      error: "Message must be a non-empty string.",
      status: 0,
      data: normalizeEdenResponse(null),
    };
  }

  const confidentialityCheck = evaluateEdenConfidentialityRisk({
    message,
    context,
  });

  if (confidentialityCheck.blocked) {
    return {
      ok: true,
      status: 200,
      data: normalizeEdenResponse({
        reply: confidentialityCheck.reply,
        trace: {
          guardrail: "client_confidentiality_redirect",
          reason: confidentialityCheck.reason,
        },
      }),
      raw: {
        reply: confidentialityCheck.reply,
        blockedByGuardrail: true,
        reason: confidentialityCheck.reason,
      },
    };
  }

  // The bearer is the live session token, and only while it still names the
  // account this page's Eden session belongs to. Page context can still carry
  // an earlier account's token or email, so identity fields are taken from the
  // session at send time, and the token travels only in the Authorization
  // header (never in the body). Without a confirmed account nothing is sent.
  const token = edenSessionBearer();
  const account = token ? getEdenSessionAccount() : null;
  if (!token || !account) {
    return {
      ok: false,
      status: 401,
      code: "eden_session_unconfirmed",
      error: EDEN_SESSION_UNCONFIRMED_REPLY,
      data: normalizeEdenResponse({ reply: EDEN_SESSION_UNCONFIRMED_REPLY }),
    };
  }
  const sessionGeneration = getEdenSessionGeneration();
  const identity = {
    role: account.role || context?.role || null,
    user: { ...(context?.user || {}), id: account.id, email: account.email },
    auth: { hasToken: true },
  };

  const payload = {
    message,
    context: context
      ? {
          ...context,
          ...identity,
          conversationState: {
            ...(context.conversationState || {}),
            ...edenConversationState,
          },
        }
      : { ...identity, conversationState: edenConversationState },
    meta: {
      ...(meta || {}),
      conversationState: {
        ...(meta?.conversationState || {}),
        ...edenConversationState,
      },
    },
    intent: intent || null,
  };

  const url = `${base}/api/eden/ask`;

  try {
    const res = await fetchWithTimeout(
      url,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
      timeoutMs
    );

    const text = await res.text();
    const json = (() => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    })();

    if (!res.ok) {
      const errMsg =
        json?.error || json?.message || `Eden request failed (${res.status}).`;

      return {
        ok: false,
        status: res.status,
        error: errMsg,
        raw: json || text,
        data: normalizeEdenResponse(json || { reply: errMsg }),
      };
    }

    // A reply that returns after its Eden session ended carries nothing forward.
    if (
      json?.conversationState &&
      typeof json.conversationState === "object" &&
      isCurrentEdenSession(sessionGeneration)
    ) {
      edenConversationState = {
        ...edenConversationState,
        ...json.conversationState,
      };
    }

    // Always return normalized contract
    return {
      ok: true,
      status: res.status,
      data: normalizeEdenResponse(json || text),
      raw: json || text,
    };
  } catch (err) {
    const isAbort = String(err?.name || "").toLowerCase().includes("abort");
    const errMsg = isAbort
      ? "Eden request timed out."
      : "Network error calling Eden.";

    return {
      ok: false,
      status: 0,
      error: errMsg,
      raw: String(err?.message || err),
      data: normalizeEdenResponse({ reply: errMsg }),
    };
  }
}

const edenClient = { edenAsk };

export default edenClient;
